import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Plus, Search, Filter, MoreVertical, Edit2, Trash2,
  Loader2, AlertCircle, Package, ArrowUpDown, CheckSquare,
  XSquare, Archive, Eye,
} from 'lucide-react';
import { supabase } from '@/utils/supabase';

const STATUS_OPTIONS = ['all', 'active', 'draft', 'archived'];

function StatusBadge({ status }) {
  const map = {
    active:   'text-emerald-400 bg-emerald-400/10',
    draft:    'text-[var(--muted)] bg-white/5',
    archived: 'text-red-400 bg-red-400/10',
  };
  return (
    <span className={`inline-block px-2 py-0.5 text-[0.58rem] font-['Space_Mono'] tracking-[0.08em] uppercase ${map[status] || map.draft}`}>
      {status}
    </span>
  );
}

function ActionMenu({ product, onEdit, onDelete, onStatusChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((p) => !p)}
        className="p-1.5 text-[var(--muted)] hover:text-[var(--ink)] transition-colors"
      >
        <MoreVertical size={13} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-40 bg-[var(--paper)] border border-[var(--line)] z-20 py-1">
          <button
            onClick={() => { onEdit(product.id); setOpen(false); }}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-[var(--ink)] hover:bg-white/5 transition-colors font-['Space_Grotesk']"
          >
            <Edit2 size={12} /> Edit
          </button>
          {product.status !== 'active' && (
            <button
              onClick={() => { onStatusChange(product.id, 'active'); setOpen(false); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs text-emerald-400 hover:bg-white/5 transition-colors font-['Space_Grotesk']"
            >
              <CheckSquare size={12} /> Set Active
            </button>
          )}
          {product.status !== 'draft' && (
            <button
              onClick={() => { onStatusChange(product.id, 'draft'); setOpen(false); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs text-[var(--muted)] hover:bg-white/5 transition-colors font-['Space_Grotesk']"
            >
              <XSquare size={12} /> Set Draft
            </button>
          )}
          {product.status !== 'archived' && (
            <button
              onClick={() => { onStatusChange(product.id, 'archived'); setOpen(false); }}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs text-amber-400 hover:bg-white/5 transition-colors font-['Space_Grotesk']"
            >
              <Archive size={12} /> Archive
            </button>
          )}
          <div className="my-1 border-t border-[var(--line)]" />
          <button
            onClick={() => { onDelete(product.id); setOpen(false); }}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-400 hover:bg-white/5 transition-colors font-['Space_Grotesk']"
          >
            <Trash2 size={12} /> Delete
          </button>
        </div>
      )}
    </div>
  );
}

export default function AdminProducts() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [search, setSearch]     = useState('');
  const [status, setStatus]     = useState('all');
  const [selected, setSelected] = useState(new Set());
  const [page, setPage]         = useState(0);
  const PAGE_SIZE = 20;

  useEffect(() => { loadProducts(); }, [search, status]);

  const loadProducts = async () => {
    setLoading(true);
    setError('');
    try {
      let query = supabase
        .from('products')
        .select('*')
        .order('created_at', { ascending: false });

      if (status !== 'all') query = query.eq('status', status);
      if (search) query = query.ilike('name', `%${search}%`);

      const { data, error: qErr } = await query;
      if (qErr) throw qErr;
      setProducts(data || []);
      setPage(0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    await supabase.from('products').update({ status: newStatus }).eq('id', id);
    setProducts((prev) => prev.map((p) => p.id === id ? { ...p, status: newStatus } : p));
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product? This cannot be undone.')) return;
    const { error: dErr } = await supabase.from('products').delete().eq('id', id);
    if (dErr) { alert(dErr.message); return; }
    setProducts((prev) => prev.filter((p) => p.id !== id));
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selected.size} products? This cannot be undone.`)) return;
    const ids = [...selected];
    const { error: dErr } = await supabase.from('products').delete().in('id', ids);
    if (dErr) { alert(dErr.message); return; }
    setProducts((prev) => prev.filter((p) => !selected.has(p.id)));
    setSelected(new Set());
  };

  const handleBulkStatus = async (newStatus) => {
    const ids = [...selected];
    await supabase.from('products').update({ status: newStatus }).in('id', ids);
    setProducts((prev) => prev.map((p) => selected.has(p.id) ? { ...p, status: newStatus } : p));
    setSelected(new Set());
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected((prev) =>
      prev.size === paged.length ? new Set() : new Set(paged.map((p) => p.id))
    );
  };

  const paged = products.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = Math.ceil(products.length / PAGE_SIZE);

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl text-[var(--ink)]">Products</h1>
          <p className="text-[var(--muted)] text-xs font-['Space_Mono'] mt-0.5">
            {products.length} product{products.length !== 1 ? 's' : ''}
          </p>
        </div>
        <Link
          to="/admin/products/new"
          className="btn-brand px-4 py-2.5 flex items-center gap-2 text-[0.65rem]"
        >
          <Plus size={12} />
          Add Product
        </Link>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-48">
          <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search products…"
            className="w-full field-surface pl-8 pr-3 py-2 text-sm focus:outline-none focus:border-[var(--brand)] transition-colors"
          />
        </div>
        <div className="flex gap-1">
          {STATUS_OPTIONS.map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              className={`px-3 py-2 text-[0.6rem] font-['Space_Mono'] tracking-[0.1em] uppercase transition-colors ${
                status === s
                  ? 'bg-[var(--brand)] text-[#09090b]'
                  : 'border border-[var(--line)] text-[var(--muted)] hover:text-[var(--ink)]'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Bulk actions */}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 px-4 py-2 bg-[var(--brand)]/10 border border-[var(--brand)]/30">
          <span className="text-xs font-['Space_Mono'] text-[var(--brand)]">{selected.size} selected</span>
          <button onClick={() => handleBulkStatus('active')} className="text-xs text-emerald-400 hover:underline font-['Space_Mono']">
            Set Active
          </button>
          <button onClick={() => handleBulkStatus('draft')} className="text-xs text-[var(--muted)] hover:underline font-['Space_Mono']">
            Set Draft
          </button>
          <button onClick={handleBulkDelete} className="text-xs text-red-400 hover:underline font-['Space_Mono']">
            Delete
          </button>
          <button onClick={() => setSelected(new Set())} className="ml-auto text-xs text-[var(--muted)] hover:text-[var(--ink)] font-['Space_Mono']">
            Clear
          </button>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 text-red-400 text-xs font-['Space_Mono'] p-3 bg-red-400/10 border border-red-400/20">
          <AlertCircle size={12} />
          <span>{error}</span>
        </div>
      )}

      {/* Table */}
      <div className="bg-[var(--paper)] border border-[var(--line)] overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <Loader2 size={18} className="animate-spin text-[var(--brand)]" />
          </div>
        ) : paged.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 gap-3">
            <Package size={28} className="text-[var(--muted)]" strokeWidth={1} />
            <p className="text-[var(--muted)] text-sm font-['Space_Grotesk']">No products found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-[var(--line)]">
                  <th className="w-10 px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selected.size === paged.length && paged.length > 0}
                      onChange={toggleAll}
                      className="accent-[var(--brand)]"
                    />
                  </th>
                  {['Product', 'Category', 'Price', 'Inventory', 'Status', ''].map((h) => (
                    <th key={h} className="text-left px-4 py-3 font-['Space_Mono'] text-[0.58rem] tracking-[0.12em] uppercase text-[var(--muted)] whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--line)]">
                {paged.map((product) => (
                  <tr key={product.id} className="hover:bg-white/3 transition-colors">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selected.has(product.id)}
                        onChange={() => toggleSelect(product.id)}
                        className="accent-[var(--brand)]"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {product.image_url ? (
                          <img
                            src={product.image_url}
                            alt={product.name}
                            className="w-9 h-9 object-cover bg-[var(--bg)] border border-[var(--line)] shrink-0"
                          />
                        ) : (
                          <div className="w-9 h-9 bg-[var(--bg)] border border-[var(--line)] flex items-center justify-center shrink-0">
                            <Package size={12} className="text-[var(--muted)]" />
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="text-[var(--ink)] font-medium truncate max-w-[180px]">{product.name}</p>
                          {product.sku && (
                            <p className="font-['Space_Mono'] text-[0.55rem] text-[var(--muted)]">SKU: {product.sku}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-[var(--muted)]">{product.description || '—'}</td>
                    <td className="px-4 py-3 font-['Space_Mono'] text-[var(--ink)]">
                      ${(product.price / 100).toFixed(2)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`font-['Space_Mono'] ${
                        (product.inventory ?? 0) === 0 ? 'text-red-400' :
                        (product.inventory ?? 0) <= 5 ? 'text-amber-400' : 'text-[var(--ink)]'
                      }`}>
                        {product.inventory ?? '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={product.status || 'active'} />
                    </td>
                    <td className="px-4 py-3">
                      <ActionMenu
                        product={product}
                        onEdit={(id) => navigate(`/admin/products/${id}/edit`)}
                        onDelete={handleDelete}
                        onStatusChange={handleStatusChange}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-[var(--muted)] text-xs font-['Space_Mono']">
            {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, products.length)} of {products.length}
          </p>
          <div className="flex gap-1">
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                onClick={() => setPage(i)}
                className={`w-7 h-7 text-xs font-['Space_Mono'] transition-colors ${
                  i === page ? 'bg-[var(--brand)] text-[#09090b]' : 'text-[var(--muted)] hover:text-[var(--ink)] border border-[var(--line)]'
                }`}
              >
                {i + 1}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
